import PageShell from "@/components/site/PageShell";
import AuditForm from "@/components/site/AuditForm";
import { motion } from "framer-motion";
import { Star, Clock, Sparkles, Quote } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Submit your details",
    desc: "Drop your name, email and website URL using the form opposite. Takes 30 seconds.",
  },
  {
    n: "02",
    title: "We audit your site",
    desc: "A senior strategist personally reviews speed, SEO, GEO visibility, GBP, UX and competitor positioning.",
  },
  {
    n: "03",
    title: "Get your growth report",
    desc: "Inside 24 hours you'll receive a no-fluff PDF + 15-minute walkthrough video with prioritized fixes.",
  },
];

export default function BookAuditPage() {
  return (
    <PageShell testId="book-audit-page">
      <section className="relative pt-16 pb-4 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              Free Website Audit
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Book your{" "}
              <span className="text-gradient-brand">free growth audit.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              A senior EchoWings strategist will personally review your website
              and send a tailored, actionable growth report — within 24 hours.
              No obligation, no sales call required.
            </p>
            <div className="mt-8 inline-flex items-center gap-4 text-sm text-white/55">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, k) => (
                  <Star
                    key={k}
                    className="w-3.5 h-3.5 fill-cyan-400 text-cyan-400"
                  />
                ))}
              </div>
              <span>150+ audits delivered</span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-cyan-400" />
                24-hour turnaround
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <AuditForm />

      <section className="section pt-0" data-testid="audit-how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              How It Works
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Three steps. Zero friction.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`audit-how-${i}`}
                className="rounded-3xl border border-white/10 bg-white/[0.025] p-7"
              >
                <div className="font-display text-5xl font-extrabold text-gradient-brand">
                  {s.n}
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-0">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="relative rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-12">
            <Quote className="absolute top-6 right-8 w-12 h-12 text-cyan-500/10" />
            <div className="flex items-center gap-1 mb-5">
              {[...Array(5)].map((_, k) => (
                <Star key={k} className="w-4 h-4 fill-cyan-400 text-cyan-400" />
              ))}
            </div>
            <p className="font-display text-2xl md:text-3xl leading-snug">
              &ldquo;The free audit alone pointed out 3 fixes that lifted our
              calls by 38% in the first month. We hired the team that same
              week.&rdquo;
            </p>
            <div className="mt-8 text-sm text-white/55">
              <span className="text-white font-semibold">Marcus Holloway</span>{" "}
              · Founder, Apex Roofing Co.
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
